import React, { useEffect, useState, useCallback } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CommissionConfigDialog } from './CommissionConfigDialog';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Plus, Edit } from 'lucide-react';

interface CommissionConfig {
  id: string;
  payment_type: string;
  platform_commission_rate: number;
  club_commission_rate: number;
  effective_from: string;
  effective_until?: string;
}

interface CommissionRatesTableProps {
  organizationId?: string;
  clubId?: string;
  title?: string;
}

export const CommissionRatesTable: React.FC<CommissionRatesTableProps> = ({
  organizationId,
  clubId,
  title = "Commission Configuration"
}) => {
  const { toast } = useToast();
  const [configs, setConfigs] = useState<CommissionConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const loadConfigs = useCallback(async () => {
    try {
      let query = supabase
        .from('commission_configs')
        .select('*')
        .order('payment_type', { ascending: true });

      if (clubId) {
        query = query.eq('club_id', clubId);
      } else if (organizationId) {
        query = query.eq('organization_id', organizationId);
      } else {
        query = query.is('organization_id', null).is('club_id', null);
      }

      const { data, error } = await query;

      if (error) throw error;
      setConfigs(data || []);
    } catch (error) {
      console.error('Error loading commission configs:', error);
      toast({
        title: 'Error',
        description: 'Failed to load commission configurations',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, clubId, toast]);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  const formatRate = (rate: number) => `${(rate * 100).toFixed(2)}%`;

  const getPaymentTypeLabel = (type: string) => {
    switch (type) {
      case 'club_fees': return 'Club Fees';
      case 'coach_salary': return 'Coach Salary';
      case 'coach_clinic': return 'Coach Clinic';
      default: return type;
    }
  };

  const getStatusBadge = (effectiveFrom: string, effectiveUntil?: string) => {
    const now = new Date();
    const from = new Date(effectiveFrom);
    const until = effectiveUntil ? new Date(effectiveUntil) : null;

    if (from > now) {
      return <Badge variant="secondary">Scheduled</Badge>;
    } else if (until && until < now) {
      return <Badge variant="outline">Expired</Badge>;
    } else {
      return <Badge variant="default">Active</Badge>;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>{title}</CardTitle>
            <CardDescription>
              Manage commission rates for different payment types
            </CardDescription>
          </div>
          <Button onClick={() => setIsDialogOpen(true)} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Add Configuration
          </Button>
        </div>
      </CardHeader>
      
      <CardContent>
        {isLoading ? (
          <div className="text-center py-4">Loading configurations...</div>
        ) : configs.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No commission configurations found. Add one to get started.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Payment Type</TableHead>
                <TableHead>Platform Rate</TableHead>
                <TableHead>Club Rate</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Effective From</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {configs.map((config) => (
                <TableRow key={config.id}>
                  <TableCell className="font-medium">
                    {getPaymentTypeLabel(config.payment_type)}
                  </TableCell>
                  <TableCell>{formatRate(config.platform_commission_rate)}</TableCell>
                  <TableCell>{formatRate(config.club_commission_rate)}</TableCell>
                  <TableCell>
                    {getStatusBadge(config.effective_from, config.effective_until)}
                  </TableCell>
                  <TableCell>
                    {new Date(config.effective_from).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm">
                      <Edit className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <CommissionConfigDialog
          isOpen={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          organizationId={organizationId}
          clubId={clubId}
          onSuccess={loadConfigs}
        />
      </CardContent>
    </Card>
  );
};